import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Receipt, CheckCircle, XCircle, User, Stethoscope, Banknote, Printer } from 'lucide-react';
import { caisseApi } from '../../services/caisseApi';
import { facturationService } from '../../services/api';
import type { BonConsultation } from '../../types/caisse';
import { formatCurrency } from '../../utils/formatters';

const num = (s: string | number | null | undefined) => Number(s ?? 0);

const MODES: { value: string; label: string }[] = [
  { value: 'especes', label: 'Espèces' },
  { value: 'mobile', label: 'Mobile Money' },
  { value: 'carte', label: 'Carte bancaire' },
  { value: 'cheque', label: 'Chèque' },
  { value: 'virement', label: 'Virement' },
];

const BonsEnAttente: React.FC = () => {
  const queryClient = useQueryClient();
  const [bonAEncaisser, setBonAEncaisser] = useState<BonConsultation | null>(null);

  const { data: bons = [], isLoading } = useQuery(
    ['caisse', 'bons-en-attente'],
    () => caisseApi.getBonsEnAttente(),
  );

  const refresh = () => {
    queryClient.invalidateQueries(['caisse', 'bons-en-attente']);
    queryClient.invalidateQueries(['caisse', 'folio']);
  };

  const annuler = useMutation(
    (id: number) => caisseApi.annulerBon(id),
    { onSuccess: refresh },
  );

  if (isLoading) {
    return <div className="p-6 text-gray-500">Chargement des bons…</div>;
  }

  return (
    <div className="w-full">
      {bons.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-10 text-center text-gray-400">
          <Receipt className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          Aucun bon de consultation en attente d'encaissement.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {bons.map((bon) => (
            <div key={bon.id} className="bg-white rounded-xl shadow-sm p-4 flex flex-col">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{bon.numero}</p>
                  <p className="text-xs text-gray-400">
                    {bon.date_consultation
                      ? new Date(bon.date_consultation).toLocaleDateString('fr-FR')
                      : '—'}
                  </p>
                </div>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                  {bon.statut_display}
                </span>
              </div>

              <div className="mt-3 space-y-1.5 text-sm text-gray-600">
                <p className="flex items-center gap-2"><User className="h-4 w-4 text-gray-400" /> {bon.patient_nom}</p>
                {bon.doctor_nom && (
                  <p className="flex items-center gap-2"><Stethoscope className="h-4 w-4 text-gray-400" /> {bon.doctor_nom}</p>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-gray-100 flex items-end justify-between">
                <div>
                  <p className="text-xs text-gray-400">
                    {num(bon.montant_restant ?? bon.montant_total) < num(bon.montant_total) ? 'Restant dû' : 'Montant'}
                  </p>
                  <p className="text-lg font-bold text-gray-900">
                    {formatCurrency(num(bon.montant_restant ?? bon.montant_total))}
                  </p>
                  {num(bon.montant_restant ?? bon.montant_total) < num(bon.montant_total) && (
                    <p className="text-xs text-gray-400">sur {formatCurrency(num(bon.montant_total))}</p>
                  )}
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => setBonAEncaisser(bon)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3 py-2 rounded-lg"
                >
                  <Banknote className="h-4 w-4" /> Encaisser
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(`Annuler le bon ${bon.numero} ?`)) annuler.mutate(bon.id);
                  }}
                  className="inline-flex items-center justify-center gap-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium px-3 py-2 rounded-lg"
                >
                  <XCircle className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {bonAEncaisser && (
        <EncaisserModal
          bon={bonAEncaisser}
          onClose={() => setBonAEncaisser(null)}
          onDone={() => {
            setBonAEncaisser(null);
            refresh();
          }}
        />
      )}
    </div>
  );
};

const EncaisserModal: React.FC<{
  bon: BonConsultation;
  onClose: () => void;
  onDone: () => void;
}> = ({ bon, onClose, onDone }) => {
  const [mode, setMode] = useState('especes');
  // Après encaissement, la caisse reste sur place le temps d'éditer le reçu
  const [encaisse, setEncaisse] = useState<{ paiementId: number; factureNumero: string } | null>(null);
  const [erreurRecu, setErreurRecu] = useState<string | null>(null);

  const mutation = useMutation(
    () => caisseApi.encaisserBon(bon.id, { mode_paiement: mode as any }),
    {
      onSuccess: (data) =>
        setEncaisse({ paiementId: data.paiement_id, factureNumero: data.facture_numero }),
    },
  );

  const imprimerRecu = async () => {
    if (!encaisse) return;
    try {
      const response = await facturationService.imprimerRecuPaiement(encaisse.paiementId);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const onglet = window.open(url, '_blank');
      if (!onglet) {
        setErreurRecu("L'ouverture du reçu a été bloquée par le navigateur : autorisez les fenêtres surgissantes.");
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch {
      setErreurRecu("L'édition du reçu a échoué.");
    }
  };

  if (encaisse) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Encaissement enregistré</h3>
            <p className="text-sm text-gray-500 mt-0.5">Facture {encaisse.factureNumero}</p>
          </div>
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm font-medium">
                {formatCurrency(num(bon.montant_restant ?? bon.montant_total))} encaissés
              </span>
            </div>
            <p className="text-sm text-gray-500">
              Le reçu de paiement peut être remis au patient.
            </p>
            {erreurRecu && <p className="text-sm text-red-600">{erreurRecu}</p>}
          </div>
          <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
            <button onClick={onDone} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
              Fermer
            </button>
            <button
              onClick={imprimerRecu}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg"
            >
              <Printer className="h-4 w-4" />
              Imprimer le reçu
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Encaisser le bon {bon.numero}</h3>
          <p className="text-sm text-gray-500 mt-0.5">{bon.patient_nom}</p>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-500">Montant à encaisser</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(num(bon.montant_restant ?? bon.montant_total))}
            </p>
            {bon.facture_numero && (
              <p className="text-xs text-gray-400 mt-1">Facture {bon.facture_numero}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700">
              {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Un paiement en espèces alimente automatiquement le folio de caisse du jour.
            </p>
          </div>
          {mutation.isError && (
            <p className="text-sm text-red-600">Erreur lors de l'encaissement.</p>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Annuler</button>
          <button
            disabled={mutation.isLoading}
            onClick={() => mutation.mutate()}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50"
          >
            <CheckCircle className="h-4 w-4" />
            {mutation.isLoading ? 'Encaissement…' : 'Confirmer & facturer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BonsEnAttente;
